import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import { buildSearchParamsKey, buildSuggestedDatesParamsKey } from 'lib/search/searchUtils'
import moment from 'moment'
import { useMemo } from 'react'
import { connect } from 'react-redux'
import { getSuggestedDates } from 'selectors/offerSelectors'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import BedbankMapSearchCard from './BedbankSearchMapCard'
import LeSearchMapCard from './LeSearchMapCard'

interface MappedStateProps {
  suggestedDates?: App.OfferSuggestedDates
  bestPrices?: {
    [key: string]: App.OfferMaybeAvailableRate
  }
}

interface Props {
  offer:
    | App.HotelOffer
    | App.HotelOfferSummary
    | App.BedbankOffer
    | App.BedbankOfferSummary
  filters?: App.OfferListFilters
  onClose?: () => void
}

function SearchMapCard(props: Props & MappedStateProps) {
  const { offer, filters, suggestedDates, onClose, bestPrices } = props

  const searchKey = useMemo(() => buildSearchParamsKey(filters?.checkIn, filters?.checkOut, filters?.rooms), [filters])
  const bestPrice = bestPrices?.[searchKey]
  const available = bestPrice?.available
  const rate = available ? bestPrice.rate : undefined

  const isBedbank = isBedbankOffer(offer)

  const offerMetaData = useOfferMetaData(offer.id, filters)
  const offerUrl = useOfferUrl(offer, {
    filters,
    bundledOfferId: offerMetaData?.bundledOfferId,
    offerLinkIncludesFilters: true,
  })

  const filtersToSearch = {
    ...filters,
    checkIn: filters?.checkIn ?? suggestedDates?.checkIn,
    checkOut: filters?.checkOut ?? suggestedDates?.checkOut,
  }

  const checkInDate = filters?.checkIn ? moment(filters.checkIn) : undefined
  const checkOutDate = filters?.checkOut ? moment(filters.checkOut) : undefined

  const { bestPricePackage, loading: fetchingPrice } = useOffersBestPrices(
    offer,
    filtersToSearch,
  )

  return (
    <div data-testid={`map-search-tile-${offer.id}`}>
      {!isBedbank && (
        <LeSearchMapCard
          onClose={onClose}
          offer={offer}
          checkInDate={checkInDate}
          filters={filters}
          offerUrl={offerUrl}
          bestPriceForDates={rate}
          bestPricePackage={bestPricePackage}
          fetchingPrice={fetchingPrice}
        />
      )}
      {isBedbank && (
        <BedbankMapSearchCard
          onClose={onClose}
          filters={filters}
          offer={offer}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
        />
      )}
    </div>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>(
  (appState, ownProps): MappedStateProps => {
    const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(
      ownProps.filters?.flexibleMonths,
      ownProps.filters?.flexibleNights,
      ownProps.filters?.rooms,
    )
    return {
      suggestedDates: getSuggestedDates(
        appState,
        flexibleSearchFilterKey,
        ownProps.offer.id,
      ),
      bestPrices: appState.offer.offerBestPrices[ownProps.offer.id],
    }
  },
)(SearchMapCard)
